// components/crm/dialogs/AddLeadDialog.jsx
import { AcademicCapIcon, CalendarDaysIcon, IdentificationIcon, PhoneArrowUpRightIcon, PlusIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../../lib/api';
import { formatBatchTimings } from '../../../lib/batchTimings';
import { validatePhone, validateEmail } from '../../../lib/phoneEmailValidation';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogTrigger } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import CourseSearchSelect from '../../ui/CourseSearchSelect';
import { Textarea } from '../../ui/textarea';

const QUALIFICATION_OPTIONS = [
  '10th / SSC',
  '12th / Intermediate',
  'Diploma',
  "Bachelor's Degree (B.Tech / B.E / B.Sc / BCA / B.Com / BA)",
  "Master's Degree (M.Tech / M.Sc / MCA / MBA)",
  'PhD',
  'Other',
];

const YEAR_OPTIONS = Array.from({ length: 15 }, (_, i) => String(new Date().getFullYear() - i));

const EMPTY_LEAD = {
  name: '',
  email: '',
  phone: '',
  interestCourse: '',
  interestBatch: '',
  source: 'website',
  priority: 'medium',
  status: 'new',
  followUpDate: '',
  notes: '',
  websiteLink: '',
  qualification: '',
  yearOfPassedOut: '',
  batchTimings: '',
  preferredMode: '',
  referencePersonName: '',
};

/** Thin horizontal section divider with centred label */
const SectionLabel = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 pt-1">
    <div className="h-px flex-1 bg-gray-100" />
    <span className="flex items-center gap-1 text-[10px] font-semibold text-violet-600 uppercase tracking-widest px-2">
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </span>
    <div className="h-px flex-1 bg-gray-100" />
  </div>
);

export default function AddLeadDialog({ open, onOpenChange, courses, coursesLoading = false, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [newLead, setNewLead] = useState(EMPTY_LEAD);

  useEffect(() => {
    if (!newLead.interestCourse) {
      setBatches([]);
      setNewLead(f => ({ ...f, interestBatch: '', batchTimings: '' }));
      return;
    }
    api.get('/batches', { params: { courseId: newLead.interestCourse, limit: 100 } })
      .then(r => {
        const all = r.data?.batches || r.data?.data || (Array.isArray(r.data) ? r.data : []);
        setBatches(all.filter(b => {
          const id = b.courseId?._id || b.courseId;
          return String(id) === String(newLead.interestCourse);
        }));
      })
      .catch(() => setBatches([]));
    setNewLead(f => ({ ...f, interestBatch: '', batchTimings: '' }));
  }, [newLead.interestCourse]);

  useEffect(() => {
    if (!newLead.interestBatch) { setNewLead(f => ({ ...f, batchTimings: '' })); return; }
    const batch = batches.find(b => b._id === newLead.interestBatch);
    if (batch) {
      setNewLead(f => ({ ...f, batchTimings: formatBatchTimings(batch.schedule) || '' }));
    }
  }, [newLead.interestBatch, batches]);

  const set  = key => val => setNewLead(f => ({ ...f, [key]: val }));
  const setE = key => e  => setNewLead(f => ({ ...f, [key]: e.target.value }));

  const handleAddLead = async (e) => {
    e.preventDefault();

    const phoneResult = validatePhone(newLead.phone);
    if (!phoneResult.valid) { toast.error(phoneResult.message); return; }

    const emailResult = validateEmail(newLead.email);
    if (!emailResult.valid) { toast.error(emailResult.message); return; }

    setLoading(true);
    try {
      await api.post('/leads', {
        ...newLead,
        phone: phoneResult.cleaned,
        email: emailResult.cleaned || undefined,
        followUpDate: newLead.followUpDate || undefined,
        interestCourse: newLead.interestCourse || undefined,
        interestBatch: newLead.interestBatch || undefined,
      });
      toast.success('Lead added successfully!');
      setNewLead(EMPTY_LEAD);
      setBatches([]);
      onSuccess();
    } catch (error) {
      console.error('Error adding lead:', error);
      toast.error(getApiErrorMessage(error, 'Failed to add lead'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white">
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Lead
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-[95vw] sm:max-w-[600px] md:max-w-[680px] p-0 gap-0 max-h-[92vh] overflow-hidden">

        {/* ── Gradient Header ───────────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <UserPlusIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white leading-tight">Add New Lead</h2>
              <p className="text-xs text-violet-200 mt-0.5">Fill in the details to create a new CRM lead</p>
            </div>
          </div>
        </div>

        {/* ── Form Body ─────────────────────────────────────────────────────────── */}
        <form onSubmit={handleAddLead} className="flex flex-col">
          <div className="overflow-y-auto popup-scroll px-6 py-4 space-y-4 max-h-[calc(92vh-148px)]">

            {/* Contact Information */}
            <SectionLabel icon={IdentificationIcon} label="Contact Information" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Full Name <span className="text-red-500">*</span></Label>
                <Input className="h-9 text-sm" placeholder="Enter full name" value={newLead.name} onChange={setE('name')} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Phone <span className="text-red-500">*</span></Label>
                <Input className="h-9 text-sm" placeholder="9876543210 or 9876543210/8765432109" value={newLead.phone} onChange={setE('phone')} required />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-medium text-gray-700">Email Address</Label>
                <Input className="h-9 text-sm" type="text" placeholder="name@example.com or email1/email2" value={newLead.email} onChange={setE('email')} />
              </div>
            </div>

            {/* Academic Background */}
            <SectionLabel icon={AcademicCapIcon} label="Academic Background" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Qualification</Label>
                <Select value={newLead.qualification} onValueChange={set('qualification')}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select qualification" /></SelectTrigger>
                  <SelectContent>
                    {QUALIFICATION_OPTIONS.map(q => <SelectItem key={q} value={q} className="text-sm">{q}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Year of Passed Out</Label>
                <Input
                  className="h-9 text-sm"
                  type="number"
                  min="1980"
                  max={new Date().getFullYear()}
                  placeholder="e.g. 2022"
                  value={newLead.yearOfPassedOut}
                  onChange={setE('yearOfPassedOut')}
                />
              </div>
            </div>

            {/* Course Interest */}
            <SectionLabel icon={PhoneArrowUpRightIcon} label="Course Interest" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Interested Course <span className="text-red-500">*</span></Label>
                <CourseSearchSelect
                  courses={courses}
                  loading={coursesLoading}
                  value={newLead.interestCourse}
                  onChange={set('interestCourse')}
                  placeholder="Select course"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Preferred Batch</Label>
                <Select
                  value={newLead.interestBatch}
                  onValueChange={set('interestBatch')}
                  disabled={!newLead.interestCourse || batches.length === 0}
                >
                  <SelectTrigger className="h-9 text-sm truncate">
                    <SelectValue placeholder={!newLead.interestCourse ? 'Select course first' : batches.length === 0 ? 'No batches available' : 'Select batch'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {batches.map(b => {
                      const modeIcon = { online: '💻', offline: '🏫', weekend: '📅', hybrid: '🔄' }[b.mode] ?? '📋';
                      const modeLabel = b.mode ? b.mode.charAt(0).toUpperCase() + b.mode.slice(1) : '';
                      const timing = formatBatchTimings(b.schedule);
                      const startDate = b.startDate ? new Date(b.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
                      return (
                        <SelectItem key={b._id} value={b._id} className="py-2">
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-sm font-medium text-gray-800">
                              {modeIcon} {modeLabel}{startDate ? ` — ${startDate}` : ''}
                            </span>
                            {timing && (
                              <span className="text-xs text-gray-500">{timing}</span>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Auto-fill batch timings banner */}
            {newLead.batchTimings && (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm">
                <span>🕐</span>
                <div>
                  <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Batch Timings: </span>
                  <span className="text-blue-800 font-medium">{newLead.batchTimings}</span>
                </div>
              </div>
            )}

            {/* Preferred mode pills */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-700">Preferred Mode</Label>
              <div className="flex gap-2" role="radiogroup" aria-label="Preferred Mode">
                {[
                  { value: 'offline', icon: '🏫', label: 'Offline' },
                  { value: 'online',  icon: '💻', label: 'Online'  },
                  { value: 'weekend', icon: '📅', label: 'Weekend' },
                ].map(({ value, icon, label }) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={newLead.preferredMode === value}
                    onClick={() => set('preferredMode')(value)}
                    className={`flex-1 flex items-center justify-center gap-1.5 border-2 rounded-lg py-2 cursor-pointer text-sm font-medium transition-all ${
                      newLead.preferredMode === value
                        ? 'border-violet-500 bg-violet-50 text-violet-700'
                        : 'border-gray-200 text-gray-600 hover:border-violet-300 hover:bg-violet-50/50'
                    }`}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Lead Details */}
            <SectionLabel icon={IdentificationIcon} label="Lead Details" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Lead Source</Label>
                <Select value={newLead.source} onValueChange={set('source')}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone_enquiry" className="text-sm">📞 Phone Enquiry</SelectItem>
                    <SelectItem value="website"       className="text-sm">🌐 Website / Google</SelectItem>
                    <SelectItem value="social"        className="text-sm">📱 Social Media</SelectItem>
                    <SelectItem value="reference"     className="text-sm">👥 Friend / Reference</SelectItem>
                    <SelectItem value="walkin"        className="text-sm">🚶 Walk-in</SelectItem>
                    <SelectItem value="other"         className="text-sm">✦ Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Priority</Label>
                <Select value={newLead.priority} onValueChange={set('priority')}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high"   className="text-sm">🔴 High</SelectItem>
                    <SelectItem value="medium" className="text-sm">🟡 Medium</SelectItem>
                    <SelectItem value="low"    className="text-sm">🟢 Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Reference Person</Label>
                <Input className="h-9 text-sm" placeholder="Who referred this lead?" value={newLead.referencePersonName} onChange={setE('referencePersonName')} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Initial Status</Label>
                <Select value={newLead.status} onValueChange={set('status')}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new"                  className="text-sm">New</SelectItem>
                    <SelectItem value="open"                 className="text-sm">Open</SelectItem>
                    <SelectItem value="contacted"            className="text-sm">Contacted</SelectItem>
                    <SelectItem value="inprogress"           className="text-sm">In Progress</SelectItem>
                    <SelectItem value="demo_scheduled"       className="text-sm">Attending Demo</SelectItem>
                    <SelectItem value="attended"             className="text-sm">Attended</SelectItem>
                    <SelectItem value="awaiting_decision"    className="text-sm">Awaiting Decision</SelectItem>
                    <SelectItem value="interested"           className="text-sm">Interested</SelectItem>
                    <SelectItem value="not_interested"       className="text-sm">Not Interested</SelectItem>
                    <SelectItem value="enrolled_with_others" className="text-sm">Enrolled with Others</SelectItem>
                    <SelectItem value="not_responding"       className="text-sm">Not Responding</SelectItem>
                    <SelectItem value="enrolled"             className="text-sm">Enrolled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Follow-up & Notes */}
            <SectionLabel icon={CalendarDaysIcon} label="Follow-up & Notes" />
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Follow Up Date</Label>
                <Input className="h-9 text-sm" type="datetime-local" value={newLead.followUpDate} onChange={setE('followUpDate')} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Initial Notes</Label>
                <Textarea
                  value={newLead.notes}
                  onChange={setE('notes')}
                  placeholder="Enter initial notes about the lead…"
                  className="text-sm min-h-[80px] resize-none"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700">Website / Reference URL</Label>
                <Input className="h-9 text-sm" type="url" placeholder="https://…" value={newLead.websiteLink} onChange={setE('websiteLink')} />
              </div>
            </div>

          </div>

          {/* ── Footer ────────────────────────────────────────────────────────── */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/80 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-sm"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-sm px-6 gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Adding…
                </>
              ) : (
                <>
                  <UserPlusIcon className="h-3.5 w-3.5" />
                  Add Lead
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
